"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";

import Callback from "@/components/request-callback/Callback";
import RewardsSection from "@/app/_components/rewards-popup";

export default function FloatingActionsBar({
	isAuthorized,
}: {
	isAuthorized: boolean;
}) {
	const pathname = usePathname();
	const isHomePage = pathname === "/";
	const [isMobile, setIsMobile] = useState<boolean | null>(null);
	const [videoOpen, setVideoOpen] = useState(false);

	useEffect(() => {
		const check = () => setIsMobile(window.innerWidth < 768);
		check();
		window.addEventListener("resize", check);

		return () => window.removeEventListener("resize", check);
	}, []);

	useEffect(() => {
		const onOpen = () => setVideoOpen(true);
		const onClose = () => setVideoOpen(false);
		window.addEventListener("video-opened", onOpen);
		window.addEventListener("video-dismissed", onClose);
		return () => {
			window.removeEventListener("video-opened", onOpen);
			window.removeEventListener("video-dismissed", onClose);
		};
	}, []);

	// Don't render until we know the screen size — avoids double mounting
	if (isMobile === null) return null;

	// Hide on the standalone /game overlay (like onboarding)
	if (pathname?.startsWith("/game")) return null;

	// When video modal is open, drop z-index so icons sit behind the backdrop blur
	const zClass = videoOpen ? "z-40" : "z-50";

	if (isMobile) {
		return (
			<div
				className={`fixed right-4 bottom-24 ${zClass} flex flex-col items-end transition-opacity duration-200`}
				style={{ opacity: 1, pointerEvents: "none" }}
			>
				{/* pointer-events: none on wrapper — Callback's FAB button has pointer-events-auto directly on it.
				    The collapsed speed-dial items (opacity-0, ~120px tall) must NOT block scroll gestures. */}
				<div style={{ pointerEvents: "none" }}>
					<Callback embedded active />
				</div>
				{isHomePage && (
					<div className="-mt-4" style={{ pointerEvents: "none" }}>
						<RewardsSection
							embedded
							isAuthorized={isAuthorized}
							disableAutoOpen
						/>
					</div>
				)}
			</div>
		);
	}

	return (
		<div
			className={`fixed right-4 md:right-16 bottom-28 md:bottom-8 ${zClass} flex flex-col items-end gap-3`}
			style={{ pointerEvents: "none" }}
		>
			<div style={{ pointerEvents: "none" }}>
				<Callback embedded />
			</div>
			{isHomePage && (
				<div style={{ pointerEvents: "none" }}>
					<RewardsSection isAuthorized={isAuthorized} />
				</div>
			)}
		</div>
	);
}
